import { useOffsiteConfig } from "@/engine/app/offsite-context";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, ApiError } from "@/api/client";
import { useStore } from "@/state/store";
import { Eyebrow } from "@/components/primitives/Eyebrow";
import { Heading } from "@/components/primitives/Heading";

/**
 * Beacon Mobile-Login (`/m/login`).
 *
 * 4-stelliges PIN-Pad (briefing/04-screen-inventory.md Screen 01).
 * Backend ist (Stand 2026-04) weiterhin der existierende
 * `POST /api/auth/login` Endpoint — die PIN wird als `password` gepostet.
 * Wenn das Backend später auf einen dedizierten PIN-Endpoint umzieht,
 * ist das ein einzelner Wechsel von `api.auth.login(pin)` auf
 * `api.auth.pin(pin)`.
 *
 * Auto-Login via `?pin=NNNN` aus dem QR-Code-Slide. Auch `?p=` wird
 * für Rückwärtskompat noch akzeptiert.
 *
 * `codeLogin` (Strategietag): statt der 4-stelligen Gruppen-PIN ein
 * 6-stelliger Einmal-Code aus `CODE_ALPHABET` — jeder Code funktioniert
 * genau einmal. Fehlertexte kommen dann wörtlich vom Server (401 "Code
 * unbekannt", 409 "Dieser Code wurde schon benutzt"), weil sie eine
 * konkrete, unterschiedliche Bedeutung haben — anders als beim generischen
 * Passwort-Login.
 */

const PIN_LENGTH = 4;
const FIELD_HEIGHT = 64;
const CODE_LENGTH = 6;
/** Ohne I/L/O/0/1 — auf dem Handy leicht zu verwechseln. */
const CODE_ALPHABET = "ABCDEFGHJKMNPQRSTUVWXYZ23456789";

function isErrorBody(body: unknown): body is { error: string } {
  return (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    typeof (body as { error: unknown }).error === "string"
  );
}

function cleanCode(raw: string): string {
  const upper = raw.toUpperCase();
  let out = "";
  for (const ch of upper) {
    if (CODE_ALPHABET.includes(ch)) out += ch;
  }
  return out.slice(0, CODE_LENGTH);
}

export function MobileLoginPage() {
  const { wordmark, formal, codeLogin } = useOffsiteConfig();
  const { dispatch } = useStore();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [autoToast, setAutoToast] = useState(false);
  const autoTried = useRef(false);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    document.documentElement.classList.add("skin-light");
    document.documentElement.classList.remove("skin-presenter");
    inputRef.current?.focus();
  }, []);

  // Magic-Link forwarding (User pastes /m/login?token=…).
  useEffect(() => {
    const t = params.get("token");
    if (t) {
      navigate(`/auth/magic?token=${encodeURIComponent(t)}`, { replace: true });
    }
  }, [params, navigate]);

  const tryLogin = async (value: string) => {
    setError(null);
    setLoading(true);
    try {
      const { token, role } = codeLogin
        ? await api.auth.loginCode(value)
        : await api.auth.login(value);
      dispatch({ type: "auth/login", token, role });
      const isWideViewport =
        typeof window !== "undefined" && window.innerWidth >= 1024;
      if (role === "presenter" && isWideViewport) {
        navigate("/present", { replace: true });
      } else if (role === "presenter") {
        navigate("/present", { replace: true });
      } else {
        navigate("/m", { replace: true });
      }
    } catch (err) {
      const msg = codeLogin
        ? // Code-Login: Server-Fehlertext wörtlich übernehmen ("Code
          // unbekannt" / "Dieser Code wurde schon benutzt") — der generische
          // PIN-Fallback wäre hier irreführend.
          err instanceof ApiError && isErrorBody(err.body)
          ? // Verbrauchter Code ist am Event der häufigste Fall — mit Ausweg
            // statt Sackgasse (Review-Fund 14.09.).
            err.body.error.includes("benutzt")
            ? `${err.body.error} — hol dir am Eingang eine Reservekarte.`
            : err.body.error
          : err instanceof ApiError && err.status === 429
            ? "Zu viele Versuche."
            : "Login fehlgeschlagen."
        : err instanceof ApiError && err.status === 401
          ? "PIN UNGÜLTIG"
          : err instanceof ApiError && err.status === 429
            ? "ZU VIELE VERSUCHE"
            : "LOGIN FEHLGESCHLAGEN";
      setError(msg);
      setPin("");
      inputRef.current?.focus();
    } finally {
      setLoading(false);
    }
  };

  // Auto-Login via ?pin= oder ?p=
  useEffect(() => {
    if (autoTried.current) return;
    const auto = params.get("pin") ?? params.get("p");
    if (!auto) return;
    autoTried.current = true;
    setPin(auto);
    setAutoToast(true);
    try {
      window.history.replaceState(null, "", "/m/login");
    } catch {
      /* noop */
    }
    tryLogin(auto);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params]);

  useEffect(() => {
    if (!autoToast) return;
    const id = window.setTimeout(() => setAutoToast(false), 3000);
    return () => window.clearTimeout(id);
  }, [autoToast]);

  const handleInput = (raw: string) => {
    if (codeLogin) {
      setPin(cleanCode(raw));
      setError(null);
      return;
    }
    // Backend erwartet Workshop-Passwort case-sensitive (z.B. "SVORulez").
    // Bis das Backend auf eine reine 4-Ziffern-PIN umzieht, akzeptieren wir
    // alphanumerischen Mixed-Case-Input und sparen das Trim/Whitespace.
    const cleaned = raw.replace(/\s/g, "");
    setPin(cleaned.slice(0, 32));
    setError(null);
  };

  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!pin || loading) return;
    tryLogin(pin);
  };

  const slotLength = codeLogin ? CODE_LENGTH : PIN_LENGTH;
  const slots = Array.from({ length: slotLength }, (_, i) => pin[i] ?? "");

  return (
    <main
      className="beacon-mobile-login"
      style={{
        minHeight: "100vh",
        background: "var(--paper-50)",
        color: "var(--paper-900)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding: "var(--s-7) var(--s-5)",
        gap: "var(--s-7)",
      }}
    >
      {autoToast && (
        <div
          role="status"
          className="mono"
          style={{
            position: "fixed",
            top: "var(--s-5)",
            left: "50%",
            transform: "translateX(-50%)",
            background: "var(--accent-blue)",
            color: "var(--svo-weiss, #fff)",
            padding: "var(--s-2) var(--s-4)",
            borderRadius: "var(--r-pill)",
            fontFamily: "var(--font-mono)",
            fontSize: 12,
            letterSpacing: "var(--tracking-meta)",
            textTransform: "uppercase",
          }}
        >
          ÜBER QR-CODE EINGELOGGT
        </div>
      )}

      <header
        style={{
          width: "100%",
          maxWidth: 360,
          display: "flex",
          flexDirection: "column",
          gap: "var(--s-3)",
        }}
      >
        <Eyebrow accent>{wordmark ?? "SVO · OFFSITE 2026"}</Eyebrow>
        <Heading level="h1">{formal ? "Willkommen" : "Bereit?"}</Heading>
      </header>

      <form
        onSubmit={onSubmit}
        style={{
          width: "100%",
          maxWidth: 360,
          display: "flex",
          flexDirection: "column",
          gap: "var(--s-5)",
        }}
      >
        <label style={{ display: "flex", flexDirection: "column", gap: "var(--s-3)" }}>
          <Eyebrow>{codeLogin ? "DEIN 6-STELLIGER CODE" : "4-STELLIGE PIN"}</Eyebrow>

          {/* Visuelles Slot-Layout — backed by einem versteckten Input. */}
          <button
            type="button"
            onClick={() => inputRef.current?.focus()}
            style={{
              all: "unset",
              display: "grid",
              gridTemplateColumns: `repeat(${slotLength}, 1fr)`,
              gap: "var(--s-3)",
              cursor: "text",
            }}
            aria-label={codeLogin ? "Code eingeben" : "PIN eingeben"}
          >
            {slots.map((c, i) => (
              <span
                key={i}
                style={{
                  height: FIELD_HEIGHT,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "var(--svo-weiss, #fff)",
                  border: error
                    ? "1px solid var(--signal-error)"
                    : "1px solid var(--paper-200)",
                  borderRadius: "var(--r-md)",
                  fontFamily: "var(--font-mono)",
                  fontSize: codeLogin ? 24 : 32,
                  fontWeight: 500,
                  letterSpacing: "0.2em",
                  color: "var(--paper-900)",
                  fontVariantNumeric: "tabular-nums",
                }}
              >
                {c || (i === pin.length ? "▎" : "")}
              </span>
            ))}
          </button>

          <input
            ref={inputRef}
            type="text"
            inputMode={codeLogin ? "text" : "numeric"}
            autoCapitalize={codeLogin ? "characters" : undefined}
            autoComplete="one-time-code"
            value={pin}
            onChange={(e) => handleInput(e.target.value)}
            style={{
              position: "absolute",
              left: -9999,
              opacity: 0,
              width: 1,
              height: 1,
            }}
            aria-label={codeLogin ? "Code" : "PIN"}
          />
        </label>

        {error && (
          <p
            className="mono"
            role="alert"
            style={{
              fontFamily: "var(--font-mono)",
              fontSize: 12,
              letterSpacing: "var(--tracking-meta)",
              textTransform: "uppercase",
              color: "var(--signal-error)",
              margin: 0,
            }}
          >
            {error}
          </p>
        )}

        <button
          type="submit"
          disabled={loading || !pin}
          className="mono"
          style={{
            all: "unset",
            cursor: loading || !pin ? "not-allowed" : "pointer",
            opacity: loading || !pin ? 0.5 : 1,
            background: "var(--paper-900)",
            color: "var(--svo-weiss, #fff)",
            padding: "var(--s-4) var(--s-5)",
            textAlign: "center",
            fontFamily: "var(--font-mono)",
            fontSize: 14,
            fontWeight: 600,
            letterSpacing: "var(--tracking-meta)",
            textTransform: "uppercase",
            borderRadius: "var(--r-md)",
            minHeight: 56,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {loading ? "…" : "BEITRETEN"}
        </button>
      </form>
    </main>
  );
}
